import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Gibt die effektiven Rollen des eingeloggten Benutzers zurück.
   * Backwards-kompatibel: Falls effectiveRoles nicht im User-Objekt vorhanden
   * ist (Legacy-Token), wird ein leeres Array zurückgegeben.
   * @returns {string[]}
   */
  const effectiveRoles = useMemo(
    () => (user?.effectiveRoles && Array.isArray(user.effectiveRoles) ? user.effectiveRoles : []),
    [user]
  );

  /**
   * True wenn der Benutzer ausschließlich die Rolle RENTER hat und kein
   * anderes Recht besitzt. Legacy-User ohne effectiveRoles gelten nicht als RENTER.
   * @returns {boolean}
   */
  const isRenterOnly = useMemo(
    () => effectiveRoles.length > 0 && effectiveRoles.every(r => r === 'RENTER'),
    [effectiveRoles]
  );

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    setLoading(false);
  }, []);

  const login = useCallback(async (identifier, password, tenantSlug) => {
    const data = await authService.login(identifier, password, tenantSlug);
    setUser(data.user);
    return data;
  }, []);

  const register = useCallback(async (userData, tenantSlug) => {
    const data = await authService.register(userData, tenantSlug);
    // Registration starts an unauthenticated state (no JWT issued).
    // Reflect that: only setUser if the server exceptionally returned a user object.
    setUser(data.user || null);
    return data;
  }, []);

  const exchangeOAuthCode = useCallback(async (oneTimeCode, codeVerifier) => {
    const data = await authService.exchangeOAuthCode(oneTimeCode, codeVerifier);
    setUser(data.user);
    return data;
  }, []);

  const linkAccount = useCallback(async (linkingToken, password) => {
    const data = await authService.linkAccount(linkingToken, password);
    setUser(data.user);
    return data;
  }, []);

  const linkAccountWithOAuth = useCallback(async (linkingToken, reAuthCode, reAuthProvider, reAuthCodeVerifier) => {
    const data = await authService.linkAccountWithOAuth(linkingToken, reAuthCode, reAuthProvider, reAuthCodeVerifier);
    setUser(data.user);
    return data;
  }, []);

  const resetPassword = useCallback(async (token, newPassword, tenantSlug) => {
    const data = await authService.resetPassword(token, newPassword, tenantSlug);
    if (data.user) {
      setUser(data.user);
    }
    return data;
  }, []);

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    authService.updateStoredUser(updatedUser);
  }, []);

  const updateToken = useCallback((newToken) => {
    authService.updateStoredToken(newToken);
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    login,
    register,
    exchangeOAuthCode,
    linkAccount,
    linkAccountWithOAuth,
    resetPassword,
    updateUser,
    updateToken,
    logout,
    loading,
    effectiveRoles,
    isRenterOnly
  }), [user, login, register, exchangeOAuthCode, linkAccount, linkAccountWithOAuth,
       resetPassword, updateUser, updateToken, logout, loading, effectiveRoles, isRenterOnly]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Liefert die effektiven Rollen des eingeloggten Benutzers.
 * Gibt ein leeres Array zurück wenn kein User eingeloggt ist oder
 * der Token keine effectiveRoles enthält (Backwards-Compatibility).
 * @returns {string[]}
 */
export const useEffectiveRoles = () => {
  const { effectiveRoles } = useAuth();
  return effectiveRoles;
};

/**
 * Gibt true zurück wenn der eingeloggte Benutzer ausschließlich die
 * Rolle RENTER besitzt. Legacy-User ohne effectiveRoles-Feld gelten
 * nicht als reine Mieter.
 * @returns {boolean}
 */
export const useIsRenterOnly = () => {
  const { isRenterOnly } = useAuth();
  return isRenterOnly;
};

export default AuthContext;
